import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Grid, Paper, TextField } from "@material-ui/core";

import AccionesRapidas from "../../general/AccionesRapidas";
import TituloAccion from "../../general/TituloAccion";
import ModalSolicitud from "../components/ModalSolicitud";
import PermisosGestores from "./PermisosGestores";
import HorariosGestion from "./HorariosGestion";
import HorarioSolicitudesGestion from "./HorarioSolicitudesGestion";

import { useConsulta } from "../hooks/useConsulta";
import {
  actualizarPeriodoVigente,
  obtenerPeriodoVigente,
} from "../services/bibliotecaApi";

import fechaIcon from "../../../global/imagenes/calendar.png";
import permisosIcon from "../../../global/imagenes/lightbulb.png";
import horariosIcon from "../../../global/imagenes/reloj_fechas_limites.png";

/** El periodo es un codigo como 20252: año y semestre. */
const PERIODO_VALIDO = /^\d{4}[12]$/;

/**
 * Cambiar el periodo vigente del modulo. Es el que reciben las solicitudes
 * nuevas y el que Gestion consulta si no se elige otro.
 */
const ModalActualizarPeriodo = ({ actual, onGuardar, onCerrar }) => {
  const [periodo, setPeriodo] = useState(actual);
  const [enviando, setEnviando] = useState(false);
  const valido = PERIODO_VALIDO.test(periodo);

  const guardar = async () => {
    setEnviando(true);
    const guardado = await onGuardar(periodo);
    if (!guardado) setEnviando(false);
  };

  return (
    <ModalSolicitud
      titulo="¡ Actualizar Período !"
      ancho="xs"
      onCerrar={onCerrar}
      acciones={
        <Button color="primary" disabled={!valido || enviando} onClick={guardar}>
          Guardar
        </Button>
      }
    >
      <TextField
        fullWidth
        autoFocus
        label="Periodo"
        value={periodo}
        onChange={({ target }) => setPeriodo(target.value.trim())}
        error={Boolean(periodo) && !valido}
        helperText="Año y semestre, por ejemplo 20261"
      />
    </ModalSolicitud>
  );
};

ModalActualizarPeriodo.propTypes = {
  actual: PropTypes.string.isRequired,
  onGuardar: PropTypes.func.isRequired,
  onCerrar: PropTypes.func.isRequired,
};

/** Tarjeta de acceso rapido, como las de Administrar en Gestion Practicas. */
const Acceso = ({ imagen, titulo, descripcion, onClick }) => (
  <Grid item xs={12} sm={12} md={6}>
    <Paper elevation={1} style={{ border: "1px solid #e0e0e0" }} onClick={onClick}>
      <AccionesRapidas imagen={imagen} titulo={titulo} sub_titulo="" des_sub_titulo={descripcion} />
    </Paper>
  </Grid>
);

Acceso.propTypes = {
  imagen: PropTypes.string.isRequired,
  titulo: PropTypes.string.isRequired,
  descripcion: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * Pestaña Administrar de Gestion Biblioteca: accesos rapidos a la
 * configuracion del modulo, como en Gestion Practicas. Solo la ven el
 * administrador general y el de biblioteca.
 */
const AdministrarGestion = ({ onMensaje }) => {
  const [vista, setVista] = useState(null);
  const [version, setVersion] = useState(0);
  const { datos: periodo } = useConsulta(obtenerPeriodoVigente, [version], "");
  const [editandoPeriodo, setEditandoPeriodo] = useState(false);

  const guardarPeriodo = async (nuevo) => {
    const { ok, titulo } = await actualizarPeriodoVigente(nuevo);
    if (!ok) {
      onMensaje(titulo, "info");
      return false;
    }

    // El endpoint de genericas solo dice "Valor Modificado".
    onMensaje(`El periodo de biblioteca ahora es ${nuevo}.`, "success");

    setEditandoPeriodo(false);
    setVersion((actual) => actual + 1);
    return true;
  };

  if (vista === "permisos") {
    return <PermisosGestores onMensaje={onMensaje} volver={() => setVista(null)} />;
  }
  if (vista === "horarios") {
    return <HorariosGestion onMensaje={onMensaje} volver={() => setVista(null)} />;
  }
  if (vista === "horario-solicitudes") {
    return <HorarioSolicitudesGestion onMensaje={onMensaje} volver={() => setVista(null)} />;
  }

  return (
    <Grid container direction="row" justifyContent="center" style={{ paddingTop: 20 }}>
      <Grid item xs={12} sm={12} md={10} lg={8}>
        <TituloAccion principal="" alterno="¿ Que deseas hacer ?" />
        <Grid container direction="row" spacing={1} style={{ width: "100%" }}>
          <Acceso
            imagen={permisosIcon}
            titulo="¡ Controlar Permisos !"
            descripcion="Asigna a cada persona de biblioteca los tipos y estados que gestiona."
            onClick={() => setVista("permisos")}
          />
          <Acceso
            imagen={horariosIcon}
            titulo="¡ Horarios !"
            descripcion="Crea los horarios de biblioteca y quién trabaja en cada uno."
            onClick={() => setVista("horarios")}
          />
          <Acceso
            imagen={horariosIcon}
            titulo="¡ Horario de Solicitudes !"
            descripcion="Cuándo se puede radicar y en qué días y horas se pueden pedir las actividades."
            onClick={() => setVista("horario-solicitudes")}
          />
          <Acceso
            imagen={fechaIcon}
            titulo="¡ Actualizar Período !"
            descripcion={
              periodo
                ? `El periodo actual es ${periodo}.`
                : "No hay un periodo activo para biblioteca."
            }
            onClick={() => setEditandoPeriodo(true)}
          />
        </Grid>
      </Grid>

      {editandoPeriodo && (
        <ModalActualizarPeriodo
          actual={periodo}
          onGuardar={guardarPeriodo}
          onCerrar={() => setEditandoPeriodo(false)}
        />
      )}
    </Grid>
  );
};

AdministrarGestion.propTypes = { onMensaje: PropTypes.func.isRequired };

export default AdministrarGestion;
